import {
    required,
    TextInput,
    useGetIdentity,
    useRecordContext,
    SelectInput,
    CheckboxGroupInput,
} from 'react-admin';
import { User } from '../../types/types';
import { Stack } from '@mui/material';
import {
    ASSIGNABLE_ROLES,
    KNOWN_FEATURES,
    ROLE_DEFAULT_TABS,
    UserRole,
} from '../../auth/roles';

// Display overrides for features whose key does not title-case well.
const FEATURE_LABELS: Record<string, string> = { api: 'API' };

export function UsersInputs() {
    const { identity } = useGetIdentity();
    const record = useRecordContext<User>();
    const currentRole = (record?.role ?? 'user') as UserRole;

    const roleChoices = [
        ...ASSIGNABLE_ROLES.map(r => ({
            id: r,
            name: r.charAt(0).toUpperCase() + r.slice(1),
        })),
        ...(record?.role === 'admin'
            ? [{ id: 'admin' as UserRole, name: 'Administrator' }]
            : []),
    ];

    const defaultTabs = ROLE_DEFAULT_TABS[currentRole] ?? [];
    const featureChoices = KNOWN_FEATURES.map(f => {
        const isRoleDefault = defaultTabs.includes(f);
        // Title-case by default; API is an initialism, not a word.
        const label =
            FEATURE_LABELS[f] ?? f.charAt(0).toUpperCase() + f.slice(1);
        return {
            id: f,
            name: isRoleDefault ? `${label} (from role)` : label,
            disabled: isRoleDefault,
        };
    });

    return (
        <Stack gap={1} sx={{ width: '100%' }}>
            <TextInput source="first_name" validate={required()} helperText={false} />
            <TextInput source="last_name" validate={required()} helperText={false} />
            <SelectInput
                source="gender"
                choices={[
                    { id: 'male', name: 'Male' },
                    { id: 'female', name: 'Female' },
                ]}
                validate={required()}
                helperText={false}
            />
            <TextInput source="email" validate={required()} helperText={false} />

            <SelectInput
                source="role"
                choices={roleChoices}
                validate={required()}
                disabled={record?.id === identity?.id}
                helperText={false}
            />

            <SelectInput
                source="status"
                choices={[
                    { id: 'approved', name: 'Approved' },
                    { id: 'disabled', name: 'Disabled' },
                ]}
                validate={required()}
                helperText={false}
                disabled={record?.id === identity?.id}
            />

            <CheckboxGroupInput
                source="allowed_features"
                choices={featureChoices}
                helperText="Role-default tabs are shown as disabled; tick extras to grant additional tabs."
            />
        </Stack>
    );
}
